import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Download, Lock, Unlock, RefreshCcw, Trophy, Crown, GraduationCap, Calendar, ScrollText, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";

export default function Retrospective() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "faculty";

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openingText, setOpeningText] = useState("");
  const [exitText, setExitText] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/retrospective");
      setData(data);
      setOpeningText(data.survey?.opening_summary || "");
      setExitText(data.survey?.exit_summary || "");
    } catch (e) {
      if (e?.response?.status === 403) {
        setData({ blocked: true });
      } else {
        toast.error(e?.response?.data?.detail || "Could not load");
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveSurveys = async () => {
    setBusy(true);
    try {
      await api.put("/admin/retrospective/survey", { opening_survey_summary: openingText, exit_survey_summary: exitText });
      toast.success("Survey summaries saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const generateNarrative = async () => {
    setBusy(true);
    try {
      await api.post("/admin/retrospective/generate-narrative");
      toast.success("AI narrative generated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const toggleRelease = async () => {
    const release = !data.programme?.retrospective_released;
    if (release && !(await confirm({ title: "Release the retrospective to the whole cohort? Every student will be able to see leaderboard, winners, all CEO reports, mentor notes, and the survey shift.", destructive: true }))) return;
    setBusy(true);
    try {
      await api.post("/admin/retrospective/release", { released: release });
      toast.success(release ? "Released to cohort + broadcast sent" : "Hidden from cohort again");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Release failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const res = await api.get("/retrospective/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "weave-cohort-retrospective.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF export failed");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="text-sm text-[#525252]">Loading…</div>;

  if (data.blocked) {
    return (
      <div className="border-2 border-dashed border-[#E5E7EB] p-12 text-center" data-testid="retrospective-blocked">
        <Lock size={32} className="mx-auto text-[#525252]" />
        <h2 className="font-display text-3xl font-black tracking-tighter mt-4">Retrospective not yet released</h2>
        <p className="text-sm text-[#525252] mt-3 max-w-md mx-auto">Faculty will release the cohort retrospective once all reflection has been captured. Check back at the end of the week.</p>
      </div>
    );
  }

  const released = data.programme?.retrospective_released;
  const nar = data.survey?.ai_narrative || {};

  return (
    <div className="space-y-10" data-testid="retrospective-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><BarChart3 size={12} /> Cohort retrospective · {released ? "Released" : "Admin draft"}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">{data.programme?.title || "iTeams Workbook"}</h1>
          <p className="text-sm text-[#525252] mt-2 max-w-2xl">A single document pulling together leaderboard, winning submissions, every team's CEO report, every mentor's session notes, and the cohort's learning shift from opening to exit. Export as PDF for Essex Business School.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={load} variant="outline" className="rounded-none border-[#0A0A0A] h-9" data-testid="retro-refresh"><RefreshCcw size={13} className="mr-2" /> Refresh</Button>
          <Button onClick={downloadPdf} disabled={busy} className="rounded-none bg-[#0A0A0A] hover:bg-[#2596be] h-9" data-testid="retro-pdf"><Download size={13} className="mr-2" /> Download PDF</Button>
          {isAdmin && (
            <Button onClick={toggleRelease} disabled={busy} className={`rounded-none h-9 ${released ? "bg-[#A23232] hover:bg-[#8a2828]" : "bg-[#2596be] hover:bg-[#1e7da3]"}`} data-testid="retro-release">
              {released ? <><Lock size={13} className="mr-2" /> Unrelease</> : <><Unlock size={13} className="mr-2" /> Release to cohort</>}
            </Button>
          )}
        </div>
      </div>

      {/* Leaderboard summary */}
      <Section icon={Trophy} title="Final standings">
        {data.leaderboard?.rows?.length ? (
          <div className="border border-[#0A0A0A] bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-white">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider">#</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider">Team</th>
                  <th className="text-center px-3 py-2 text-[10px] uppercase tracking-wider">Wins</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider">Total AI score</th>
                </tr>
              </thead>
              <tbody>
                {data.leaderboard.rows.map((r) => (
                  <tr key={r.team_id} className={`border-t border-[#E5E7EB] ${r.rank === 1 ? "bg-[#FEF3D0]" : ""}`} data-testid={`retro-lb-row-${r.team_id}`}>
                    <td className="px-3 py-2 font-display font-black text-xl">{r.rank}</td>
                    <td className="px-3 py-2 font-semibold">{r.team_name}{r.rank === 1 && <Crown size={14} className="inline ml-2 text-[#f4b847]" />}</td>
                    <td className="px-3 py-2 text-center">{r.wins}</td>
                    <td className="px-3 py-2 text-right font-display font-black">{r.total_score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>No scored submissions yet.</Empty>}
      </Section>

      {/* Winners */}
      <Section icon={Trophy} title="Challenge winners">
        {data.winners?.some((w) => w.submission_id) ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {data.winners.map((w) => (
              <div key={w.challenge_id} className="border-l-4 border-[#f4b847] bg-[#FEF3D0] p-4" data-testid={`retro-winner-${w.challenge_id}`}>
                <div className="label-eyebrow text-[#7A5B17]">{w.challenge_title}</div>
                {w.submission_id ? (
                  <>
                    <div className="font-display font-black text-xl mt-2">{w.team_name}</div>
                    <div className="text-xs text-[#525252]">{w.user_name} · AI score {w.ai_score}</div>
                    {w.content && <p className="text-xs text-[#0A0A0A] mt-3 whitespace-pre-wrap line-clamp-6">{w.content}</p>}
                  </>
                ) : (
                  <div className="text-sm text-[#525252] italic mt-2">No winner announced</div>
                )}
              </div>
            ))}
          </div>
        ) : <Empty>No winners announced yet.</Empty>}
      </Section>

      {/* CEO reports */}
      <Section icon={ScrollText} title={`CEO reports (${data.ceo_reports?.length || 0})`}>
        {data.ceo_reports?.length ? (
          <div className="space-y-4">
            {data.ceo_reports.map((r) => (
              <div key={r.id} className="border border-[#E5E7EB] bg-white p-5" data-testid={`retro-ceo-${r.id}`}>
                <div className="font-display font-black text-xl">{r.team_name}</div>
                {["vision", "key_decisions", "challenges_faced", "next_steps", "highlights", "contributor_notes"].map((k) => r[k] && (
                  <div key={k} className="mt-3">
                    <div className="label-eyebrow">{k.replace(/_/g, " ")}</div>
                    <p className="text-sm whitespace-pre-wrap mt-1">{r[k]}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : <Empty>No CEO reports submitted yet.</Empty>}
      </Section>

      {/* Mentor notes */}
      <Section icon={GraduationCap} title={`Mentor session notes (${data.mentor_notes?.length || 0})`}>
        {data.mentor_notes?.length ? (
          <ul className="space-y-3">
            {data.mentor_notes.map((s) => (
              <li key={s.id} className="border-l-2 border-[#2596be] bg-[#E6F4FA] p-4" data-testid={`retro-note-${s.id}`}>
                <div className="flex justify-between flex-wrap gap-2">
                  <div className="font-semibold text-sm">{s.mentor_name} <span className="text-[#525252] font-normal">with {s.team_name}</span></div>
                  <div className="text-[10px] uppercase tracking-wider text-[#525252]"><Calendar size={10} className="inline mr-1" />{s.day_date} · {s.slot_start?.slice(11, 16)}</div>
                </div>
                <div className="text-sm whitespace-pre-wrap mt-2">{s.session_note}</div>
              </li>
            ))}
          </ul>
        ) : <Empty>No mentor session notes recorded yet.</Empty>}
      </Section>

      {/* Survey shift */}
      <Section icon={Sparkles} title="Cohort learning shift">
        {nar.headline && (
          <div className="border border-[#2596be] bg-[#E6F4FA] p-6 mb-4" data-testid="retro-narrative">
            <div className="font-display text-2xl font-black tracking-tighter">{nar.headline}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <NarrativeBlock label="Strongest growth" text={nar.strongest_shift} testid="retro-narrative-strongest" />
              <NarrativeBlock label="Areas still to develop" text={nar.weakest_shift} testid="retro-narrative-weakest" />
              {nar.outlier && <NarrativeBlock label="Notable outlier" text={nar.outlier} testid="retro-narrative-outlier" />}
              {nar.recommendation && <NarrativeBlock label="Recommendation" text={nar.recommendation} testid="retro-narrative-recommendation" />}
            </div>
            {data.survey?.ai_generated_at && <div className="text-[10px] uppercase tracking-wider text-[#525252] mt-4">Generated {data.survey.ai_generated_at.slice(0, 16).replace("T", " ")}</div>}
          </div>
        )}
        {isAdmin && (
          <div className="border border-[#0A0A0A] bg-white p-5 space-y-4" data-testid="retro-survey-admin">
            <div className="label-eyebrow">Admin · paste survey summaries (or raw responses)</div>
            <p className="text-xs text-[#525252]">
              Paste a paragraph summarising the opening and exit survey results. Claude will compare them to write the learning-shift narrative above.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="label-eyebrow mb-2">Opening survey summary</div>
                <Textarea
                  className="rounded-none min-h-[140px] text-sm"
                  value={openingText}
                  onChange={(e) => setOpeningText(e.target.value)}
                  placeholder="e.g. At entry, most students rated their M&A integration confidence at 2-3/5…"
                  data-testid="retro-opening-input"
                />
              </div>
              <div>
                <div className="label-eyebrow mb-2">Exit survey summary</div>
                <Textarea
                  className="rounded-none min-h-[140px] text-sm"
                  value={exitText}
                  onChange={(e) => setExitText(e.target.value)}
                  placeholder="e.g. By exit, most students self-rated 4/5 across integration confidence…"
                  data-testid="retro-exit-input"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={saveSurveys} disabled={busy} variant="outline" className="rounded-none border-[#0A0A0A]" data-testid="retro-survey-save">
                Save summaries
              </Button>
              <Button onClick={generateNarrative} disabled={busy || !openingText || !exitText} className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="retro-narrative-generate">
                <Sparkles size={13} className="mr-2" /> {busy ? "Generating…" : nar.headline ? "Re-generate narrative" : "Generate narrative"}
              </Button>
            </div>
          </div>
        )}
        {!nar.headline && !isAdmin && <Empty>No learning shift narrative published yet.</Empty>}
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="label-eyebrow flex items-center gap-2 mb-3"><Icon size={12} /> {title}</div>
      {children}
    </section>
  );
}

function Empty({ children }) {
  return <div className="border border-dashed border-[#E5E7EB] p-6 text-sm text-[#525252] text-center">{children}</div>;
}

function NarrativeBlock({ label, text, testid }) {
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow text-[#7A5B17]">{label}</div>
      <p className="text-sm mt-1 whitespace-pre-wrap">{text}</p>
    </div>
  );
}
